import React from 'react';
import PropTypes from 'prop-types';
import { createVariants } from './_variants';

const emptyStateClasses = createVariants({
  base: 'text-center mx-auto',
  variants: {
    default: '',
    error: '',
    info: '',
    warning: '',
  },
  sizes: {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
  },
  defaultVariant: 'default',
  defaultSize: 'md',
});

/**
 * EmptyState - Consistent empty/error/info states
 * 
 * Features:
 * - Icon with colored background circle
 * - Title and description
 * - Optional action button
 * - Variants for different contexts (default, error, info, warning)
 */
const EmptyState = ({
  icon,
  iconBg = 'var(--succession-bg-secondary)',
  iconColor = 'var(--succession-text-muted)',
  iconSize = 'md',
  title,
  description,
  action,
  variant = 'default',
  size = 'md',
  className = '',
}) => {
  const classes = emptyStateClasses({ variant, size, className });
  
  const iconSizes = {
    sm: { container: 'w-12 h-12', icon: 'w-6 h-6', marginBottom: 'mb-4' },
    md: { container: 'w-16 h-16', icon: 'w-8 h-8', marginBottom: 'mb-5' },
    lg: { container: 'w-20 h-20', icon: 'w-10 h-10', marginBottom: 'mb-6' },
  };
  
  const { container, icon: iconClass, marginBottom } = iconSizes[iconSize] || iconSizes.md;

  return (
    <div className={classes}>
      {icon && (
        <div 
          className={`${container} ${marginBottom} rounded-full flex items-center justify-center mx-auto border-2 border-[var(--succession-border)]`}
          style={{ backgroundColor: iconBg }}
        >
          <div className={iconClass} style={{ color: iconColor }}>
            {icon}
          </div>
        </div>
      )}
      
      {title && (
        <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">
          {title}
        </h3>
      )}
      
      {description && (
        <p className="text-sm text-[var(--succession-text-secondary)] mb-5 leading-relaxed">
          {description}
        </p>
      )}
      
      {action && action}
    </div>
  );
};

EmptyState.propTypes = {
  icon: PropTypes.node,
  iconBg: PropTypes.string,
  iconColor: PropTypes.string,
  iconSize: PropTypes.oneOf(['sm', 'md', 'lg']),
  title: PropTypes.string,
  description: PropTypes.string,
  action: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'error', 'info', 'warning']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
};

export default EmptyState;

